import { Armchair, Book, Car, Dumbbell, Laptop, Shirt, type LucideIcon } from "lucide-react";
import { Link } from "react-router";

import { Skeleton } from "@/components/ui/skeleton";
import { PUBLIC_ROUTES } from "@/constants/routes";
import type { Category } from "@/features/marketplace/types/category";

interface CategoryTileProps {
  category: Category;
}

/**
 * Fallback icon per slug, for the fixed 6-category set `server/src/scripts/
 * seed.ts` seeds today (matches the Stitch wireframe's icon-led tiles).
 * `category.iconUrl` — real artwork from the server — always wins when
 * present; this map only covers dev/seed data that has none yet. A slug
 * outside this map (a future real category) falls through to the
 * initial-letter treatment below rather than guessing an icon for it.
 */
const CATEGORY_ICONS: Record<string, LucideIcon> = {
  books: Book,
  electronics: Laptop,
  fashion: Shirt,
  "home-furniture": Armchair,
  vehicles: Car,
  "sports-fitness": Dumbbell,
};

/**
 * docs/ui/component-inventory.md `CategoryTile`: icon/image-led entry into
 * a browsing category. Links to `/search?category=<slug>` per
 * docs/ui/routes.md's decision that category browsing resolves through
 * `/search`'s query params rather than a dedicated `/categories` route.
 */
export function CategoryTile({ category }: CategoryTileProps) {
  const Icon = CATEGORY_ICONS[category.slug];

  return (
    <Link
      to={`${PUBLIC_ROUTES.search}?category=${category.slug}`}
      className="flex flex-col items-center gap-2 rounded-lg border border-border bg-card p-4 text-center transition-colors hover:bg-muted"
    >
      <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-muted">
        {category.iconUrl ? (
          <img src={category.iconUrl} alt="" className="h-full w-full object-cover" />
        ) : Icon ? (
          <Icon className="h-6 w-6 text-primary" aria-hidden="true" />
        ) : (
          <span className="text-h3 text-muted-foreground" aria-hidden="true">
            {category.name[0]}
          </span>
        )}
      </div>
      <span className="text-body-sm text-foreground">{category.name}</span>
    </Link>
  );
}

export function CategoryTileSkeleton() {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-border bg-card p-4">
      <Skeleton className="h-12 w-12 rounded-full" />
      <Skeleton className="h-4 w-14" />
    </div>
  );
}
